import React, { useEffect, useRef } from "react";

/**
 * ScrollPicker — drum-roll picker scrollabile con il dito (iOS-style).
 *
 * Implementazione:
 *  - Un container con `overflow-y: auto` + `scroll-snap-type: y mandatory`.
 *  - 3 padding-items vuoti sopra e sotto in modo che il primo/ultimo valore
 *    possano essere centrati.
 *  - Ogni item ha altezza fissa (ITEM_H) ed è uno snap-point.
 *  - Lo "scroll end" lo intercettiamo via `scrollend` (Safari iOS supportato
 *    da iOS 16+) con fallback debounce su `scroll`.
 *  - L'indice attivo è `Math.round(scrollTop / ITEM_H)`.
 */
const ITEM_H = 56;
const VISIBLE = 5; // 2 sopra + 1 centrale + 2 sotto

export default function ScrollPicker({
  values,
  value,
  onChange,
  pad = 2,
  ariaLabel,
}) {
  const ref = useRef(null);
  const debounceRef = useRef(null);

  // Sync prop -> scroll (al mount e quando value cambia dall'esterno).
  useEffect(() => {
    if (!ref.current) return;
    const idx = values.indexOf(value);
    if (idx < 0) return;
    // Scroll WITHOUT smooth se è il mount iniziale
    ref.current.scrollTo({ top: idx * ITEM_H, behavior: "auto" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!ref.current) return;
    const idx = values.indexOf(value);
    if (idx < 0) return;
    const currentIdx = Math.round(ref.current.scrollTop / ITEM_H);
    if (currentIdx !== idx) {
      ref.current.scrollTo({ top: idx * ITEM_H, behavior: "smooth" });
    }
  }, [value, values]);

  const fmt = (n) => String(n).padStart(pad, "0");

  const handleScroll = () => {
    if (!ref.current) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const el = ref.current;
      if (!el) return;
      const idx = Math.max(
        0,
        Math.min(values.length - 1, Math.round(el.scrollTop / ITEM_H))
      );
      const v = values[idx];
      if (v !== value) onChange(v);
    }, 90);
  };

  const containerHeight = ITEM_H * VISIBLE;
  const padCount = Math.floor(VISIBLE / 2);

  return (
    <div
      className="relative select-none"
      style={{ height: containerHeight, width: 92 }}
      aria-label={ariaLabel}
    >
      {/* Highlight della riga centrale (frame stile picker iOS) */}
      <div
        className="absolute left-0 right-0 pointer-events-none"
        style={{
          top: padCount * ITEM_H,
          height: ITEM_H,
          borderTop: "1px solid rgba(255,255,255,0.18)",
          borderBottom: "1px solid rgba(255,255,255,0.18)",
        }}
      />
      {/* Fade top/bottom per dare profondità */}
      <div
        className="absolute inset-x-0 top-0 pointer-events-none"
        style={{
          height: padCount * ITEM_H,
          background:
            "linear-gradient(to bottom, rgba(15,30,40,0.95) 0%, rgba(15,30,40,0) 100%)",
        }}
      />
      <div
        className="absolute inset-x-0 bottom-0 pointer-events-none"
        style={{
          height: padCount * ITEM_H,
          background:
            "linear-gradient(to top, rgba(15,30,40,0.95) 0%, rgba(15,30,40,0) 100%)",
        }}
      />
      <div
        ref={ref}
        onScroll={handleScroll}
        className="h-full overflow-y-auto no-scrollbar"
        style={{
          scrollSnapType: "y mandatory",
          WebkitOverflowScrolling: "touch",
          touchAction: "pan-y",
          overscrollBehavior: "contain",
        }}
        data-testid={`scroll-picker-${ariaLabel || "picker"}`}
      >
        {/* spacer top */}
        {Array.from({ length: padCount }).map((_, i) => (
          <div key={`pt${i}`} style={{ height: ITEM_H }} />
        ))}
        {values.map((v, i) => {
          const isActive = v === value;
          return (
            <div
              key={i}
              role="option"
              aria-selected={isActive}
              onClick={() => {
                if (ref.current) {
                  ref.current.scrollTo({ top: i * ITEM_H, behavior: "smooth" });
                }
              }}
              style={{
                height: ITEM_H,
                scrollSnapAlign: "center",
                scrollSnapStop: "always",
              }}
              className={`flex items-center justify-center text-center transition-all duration-150 ${
                isActive
                  ? "text-white text-[56px] leading-none font-light"
                  : "text-white/35 text-[26px] leading-none"
              }`}
            >
              {fmt(v)}
            </div>
          );
        })}
        {/* spacer bottom */}
        {Array.from({ length: padCount }).map((_, i) => (
          <div key={`pb${i}`} style={{ height: ITEM_H }} />
        ))}
      </div>
    </div>
  );
}
